/**
 * Configuration options for pollUntil
 */
export type PollUntilOptions = {
  /**
   * Maximum total time to wait before giving up (in milliseconds).
   * After this time, the function returns the last fetched result.
   * @default 15000
   */
  maxWaitTimeMs?: number

  /**
   * Delay before the first attempt (in milliseconds).
   * @default 2000
   */
  initialDelayMs?: number

  /**
   * Starting increment for exponential backoff after the first attempt (in milliseconds).
   * Delays grow exponentially: incrementMs → incrementMs*2 → incrementMs*4 → ...
   * @default 500
   */
  incrementMs?: number
}

/**
 * Polls an async function until a condition is met, using exponential backoff.
 *
 * This utility is useful when you need to wait for an async operation to complete
 * on the backend (e.g., a job queued to BullMQ) before the result is available.
 *
 * **How it works:**
 * 1. Waits for `initialDelayMs` before the first attempt
 * 2. Calls `fetchFn` and checks `conditionFn`
 * 3. If condition is met, returns the result
 * 4. Otherwise, waits `incrementMs`, then `incrementMs*2`, then `incrementMs*4`, etc.
 * 5. Continues until `conditionFn` is satisfied or `maxWaitTimeMs` is exceeded
 *
 * **Example timing with defaults (initialDelayMs=2000, incrementMs=500, maxWaitTimeMs=15000):**
 * ```
 * Attempt 1: wait 2000ms (initialDelayMs)  → total: 2000ms
 * Attempt 2: wait 500ms  (incrementMs)     → total: 2500ms
 * Attempt 3: wait 1000ms (incrementMs*2)   → total: 3500ms
 * Attempt 4: wait 2000ms (incrementMs*4)   → total: 5500ms
 * Attempt 5: wait 4000ms (incrementMs*8)   → total: 9500ms
 * Attempt 6: wait 5500ms (capped)          → total: 15000ms
 * ```
 *
 * @param fetchFn - Async function that fetches the current state
 * @param conditionFn - Predicate that returns true when polling should stop
 * @param options - Configuration options for timing
 * @returns The result from fetchFn, either when condition is met or after timeout
 */
export const pollUntil = async <T>(
  fetchFn: () => Promise<T>,
  conditionFn: (result: T) => boolean,
  options: PollUntilOptions = {}
): Promise<T> => {
  const {
    maxWaitTimeMs = 15000,
    initialDelayMs = 2000,
    incrementMs = 500,
  } = options

  let totalTime = 0
  let attempt = 0

  while (totalTime < maxWaitTimeMs) {
    // Calculate delay: initialDelayMs for first attempt, then exponential backoff
    let delay: number
    if (attempt === 0) {
      delay = initialDelayMs
    } else {
      delay = incrementMs * Math.pow(2, attempt - 1)
    }

    // Cap to remaining time so we don't exceed maxWaitTimeMs
    delay = Math.min(delay, maxWaitTimeMs - totalTime)
    await new Promise((resolve) => setTimeout(resolve, delay))
    totalTime += delay

    const result = await fetchFn()

    if (conditionFn(result)) {
      return result
    }

    attempt++
  }

  // Return whatever we have after timeout
  return fetchFn()
}
